import React from "react";
import { apiRequest } from "../api/awsClient";

const MIN_CHARS = 10;
const MAX_CHARS = 4000;

const ERRORS = {
  message_too_short: "Tell us a little more about what you want automated.",
  message_too_long: "That is longer than we can send. Trim it to 4,000 characters.",
  too_many_requests: "You have sent several requests in the last hour. Try again a bit later.",
  send_failed: "It did not send. Try again in a minute.",
};

/**
 * "Request a custom automation": what the built-in automations do not cover,
 * written in the customer's own words and emailed to the BossDrop team
 * (backend: server/customAutomationRequest.js). Collapsed to one button until
 * someone needs it.
 */
export default function CustomAutomationRequest() {
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  const length = message.trim().length;
  const canSend = !sending && length >= MIN_CHARS && length <= MAX_CHARS;

  async function send() {
    if (!canSend) return;
    setSending(true);
    setError("");
    try {
      await apiRequest("/api/automations/custom-request", {
        method: "POST",
        body: { message: message.trim() },
      });
      setSent(true);
      setMessage("");
      setOpen(false);
    } catch (e) {
      setError(ERRORS[e?.message] || ERRORS.send_failed);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="customAutomation">
      <div className="customAutomation__row">
        <div className="customAutomation__text">
          <div className="crFieldLabel">Need something these do not cover?</div>
          <div className="muted small">
            Describe the automation you want and it goes straight to the BossDrop
            team. We reply by email.
          </div>
        </div>
        {!open ? (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setOpen(true);
              setSent(false);
            }}
          >
            Request a custom automation
          </button>
        ) : null}
      </div>

      {sent ? (
        <div className="muted small customAutomation__sent" role="status">
          <span className="chip ok">Sent</span> The BossDrop team has your request
          and will reply by email.
        </div>
      ) : null}

      {open ? (
        <div className="customAutomation__form">
          <label className="crField">
            <span className="crFieldLabel">What should BossDrop do?</span>
            <textarea
              className="textareaFancy"
              rows={5}
              maxLength={MAX_CHARS}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="For example: every Friday, pull open deals over $10k from HubSpot and ask each owner in Slack for a next step."
              autoFocus
            />
            <span className="muted small">
              When it should run, what it should look at, and who it should
              reach are the most useful details.
            </span>
          </label>
          {error ? (
            <div className="customAutomation__error" role="alert">
              {error}
            </div>
          ) : null}
          <div className="customAutomation__actions">
            <button
              type="button"
              className="btn"
              onClick={() => {
                setOpen(false);
                setError("");
              }}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={send}
              disabled={!canSend}
            >
              {sending ? "Sending…" : "Send request"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
